import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { requireAdmin } from "@/lib/admin/require-admin.server";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { dbError } from "@/lib/db";

export interface AdminCoupon {
  id: string;
  code: string;
  discount_type: string;
  discount_value: number;
  min_order_value: number | null;
  max_uses: number | null;
  uses_count: number;
  valid_from: string;
  valid_until: string | null;
  is_active: boolean;
  created_at: string;
}

export const couponInputSchema = z.object({
  code: z
    .string()
    .min(2)
    .transform((v) => v.trim().toUpperCase()),
  discountType: z.enum(["percentage", "fixed"]),
  discountValue: z.number().positive(),
  minOrderValue: z.number().nonnegative().nullable(),
  maxUses: z.number().int().positive().nullable(),
  validUntil: z.string().nullable(),
  isActive: z.boolean(),
});
export type AdminCouponInput = z.infer<typeof couponInputSchema>;

// Handlers extraídos em funções simples: createServerFn lança "No Start
// context found" quando chamada fora de uma requisição real, então testar a
// const exportada diretamente quebra — mesmo motivo documentado em
// addresses.functions.ts.

export async function listAllCoupons(): Promise<AdminCoupon[]> {
  const { data, error } = await supabaseAdmin
    .from("coupons")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw dbError(error, "src/lib/admin/coupons.functions.ts");
  return (data ?? []) as AdminCoupon[];
}

export async function getCouponById(id: string): Promise<AdminCoupon | null> {
  const { data, error } = await supabaseAdmin
    .from("coupons")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw dbError(error, "src/lib/admin/coupons.functions.ts");
  return (data ?? null) as AdminCoupon | null;
}

// Compartilhada entre insertCoupon e editCoupon, que gravavam o mesmo
// mapeamento camelCase (schema) → snake_case (coluna) duas vezes.
function toCouponFields(data: AdminCouponInput) {
  return {
    code: data.code,
    discount_type: data.discountType,
    discount_value: data.discountValue,
    min_order_value: data.minOrderValue,
    max_uses: data.maxUses,
    valid_until: data.validUntil,
    is_active: data.isActive,
  };
}

export async function insertCoupon(data: AdminCouponInput): Promise<void> {
  const { error } = await supabaseAdmin.from("coupons").insert(toCouponFields(data));
  if (error) throw dbError(error, "src/lib/admin/coupons.functions.ts");
}

export async function editCoupon(data: AdminCouponInput & { id: string }): Promise<void> {
  const { id, ...rest } = data;
  const { error } = await supabaseAdmin.from("coupons").update(toCouponFields(rest)).eq("id", id);
  if (error) throw dbError(error, "src/lib/admin/coupons.functions.ts");
}

export async function removeCoupon(id: string): Promise<void> {
  const { error } = await supabaseAdmin.from("coupons").delete().eq("id", id);
  if (error) throw dbError(error, "src/lib/admin/coupons.functions.ts");
}

export const adminListCoupons = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(() => listAllCoupons());

export const adminGetCoupon = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .validator((id: string) => z.string().uuid().parse(id))
  .handler(async ({ data: id }) => getCouponById(id));

export const adminCreateCoupon = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((data: AdminCouponInput) => couponInputSchema.parse(data))
  .handler(async ({ data }) => insertCoupon(data));

export const adminUpdateCoupon = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((data: AdminCouponInput & { id: string }) =>
    couponInputSchema.extend({ id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ data }) => editCoupon(data));

export const adminDeleteCoupon = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((id: string) => z.string().uuid().parse(id))
  .handler(async ({ data: id }) => removeCoupon(id));

export const adminCouponsQuery = () =>
  queryOptions({ queryKey: ["admin", "coupons"], queryFn: () => adminListCoupons() });

export const adminCouponQuery = (id: string) =>
  queryOptions({ queryKey: ["admin", "coupon", id], queryFn: () => adminGetCoupon({ data: id }) });
